/**
 * AddEditPowerLogModal Component
 * Modal for creating or editing power log entries
 * Refactored to match AddEditPowerModal design patterns
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { CalendarIcon, ChevronDownIcon, ExclamationCircleIcon, CheckCircleIcon, ArrowTopRightOnSquareIcon, DocumentTextIcon, FolderOpenIcon, SparklesIcon, DocumentMinusIcon, QueueListIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import {
  createPowerLog,
  updatePowerLog,
  validatePowerNumber,
  getAllPowersForLog,
  getExecutedPowerPrefill,
  FIELD_LIMITS
} from '../../../../services/oathPowerLogService';
import {
  getActiveReports,
  createReport,
  getExecutingAgents,
  getCities,
  addPowerToReport,
  getAvailableRates
} from '../../../../services/oathPowerReportService';
import { OathModal, OathButton, OathSpinner, OathAlert, OathDualSpinner, OathConfirmDialog } from '../../../shared/OathUI';
import Tooltip from '@mui/material/Tooltip';
import DischargeModal from '../../powerinquiry/components/DischargeModal';
import { addToQueue as addToDischargeQueue, validatePowers as validateDischargePowers } from '../../../../services/oathDischargeService';
import {
  formatDateForInput,
  formatDateForDisplay,
  formatDateInputText,
  parseDateInput,
  parseCurrency,
  formatCurrency,
  formatSSN,
  getPowerStatusBadgeInfo
} from '../utils/formatters';
import { VALIDATION_RULES, isPowerReportable, RATE_PRIORITY, RATE_TYPE } from '../utils/constants';
import { useDebounce } from '../utils/hooks';
import { usePermissions } from '../../../../hooks/usePermissions';

/**
 * Attempts to match a free-text agent name against the loaded agents list.
 * Tries exact, "last, first", reversed, and partial token matching.
 */
const findMatchingAgent = (nameStr, agentsList) => {
  if (!nameStr || !agentsList?.length) return null;
  const search = nameStr.trim().toLowerCase();

  // Exact "firstName lastName"
  let match = agentsList.find(
    a => `${a.firstName} ${a.lastName}`.toLowerCase() === search
  );
  if (match) return match;

  // "lastName, firstName"
  match = agentsList.find(
    a => `${a.lastName}, ${a.firstName}`.toLowerCase() === search
  );
  if (match) return match;

  // Reversed "lastName firstName"
  match = agentsList.find(
    a => `${a.lastName} ${a.firstName}`.toLowerCase() === search
  );
  if (match) return match;

  // Partial – every token of the agent name appears somewhere in the search string
  match = agentsList.find(a => {
    const first = a.firstName?.toLowerCase();
    const last = a.lastName?.toLowerCase();
    return first && last && search.includes(first) && search.includes(last);
  });
  return match || null;
};

const INITIAL_FORM_DATA = {
  powerNo: '',
  name: '', // Full name for power log
  defendantFirstName: '', // First name for report submission
  defendantLastName: '', // Last name for report submission
  defendantSsn: '', // SSN in XXX-XX-XXXX format
  defendantDob: '', // Date of birth
  offense: '', // Offense/charge description
  dateExecuted: '',
  caseNumber: '',
  amountOfBond: '',
  premiumCharged: '',
  companyPremium: '',
  disposition: '',
  dispositionDate: '',
  collateralReceiptNo: '',
  collateral: '',
  agentsName: '',
  scAmount: '' // SC Premium for South Carolina cities
};

const AddEditPowerLogModal = ({ isOpen, onClose, log, agencyId, onSave, onRefresh, showReportAssignment = true }) => {
  const isEdit = Boolean(log);
  const { canAccessDischarges } = usePermissions();

  // Form state
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  // SC Amount display state (formatted with commas)
  const [scAmountDisplay, setScAmountDisplay] = useState('');

  // Date text input states (dual text + picker pattern)
  const [dateExecutedText, setDateExecutedText] = useState('');
  const [dispositionDateText, setDispositionDateText] = useState('');
  const [dobText, setDobText] = useState('');
  const dateExecutedPickerRef = useRef(null);
  const dispositionDatePickerRef = useRef(null);
  const dobDatePickerRef = useRef(null);

  // Validation state
  const [errors, setErrors] = useState({});
  const [powerValidation, setPowerValidation] = useState({
    isValidating: false,
    isValid: null,
    alreadyInLog: false,
    message: ''
  });
  const [selectedPowerStatus, setSelectedPowerStatus] = useState(null);

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  const firstFieldRef = useRef(null);

  // Power dropdown state (for add mode)
  const [availablePowers, setAvailablePowers] = useState([]);
  const [loadingPowers, setLoadingPowers] = useState(false);
  const [powerSearch, setPowerSearch] = useState('');
  const [showPowerDropdown, setShowPowerDropdown] = useState(false);
  const [highlightedPowerIndex, setHighlightedPowerIndex] = useState(-1);
  const powerDropdownRef = useRef(null);
  const powerListRef = useRef(null);
  const debouncedPowerSearch = useDebounce(powerSearch, 300);
  // Tracks the most recent power a prefill request was issued for, so a slower
  // response for a power the user has since changed away from is discarded.
  const prefillPowerRef = useRef(null);

  // Report assignment state (only for add mode)
  const [reportAssignment, setReportAssignment] = useState('none'); // 'none' | 'existing' | 'new'
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [activeReports, setActiveReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [showReportDropdown, setShowReportDropdown] = useState(false);
  const [reportSearch, setReportSearch] = useState('');
  const [highlightedReportIndex, setHighlightedReportIndex] = useState(-1);
  const reportDropdownRef = useRef(null);
  const reportListRef = useRef(null);

  // Agent and city state for report assignment
  const [agents, setAgents] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [agentSearch, setAgentSearch] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  const [highlightedAgentIndex, setHighlightedAgentIndex] = useState(-1);
  const [highlightedCityIndex, setHighlightedCityIndex] = useState(-1);
  const agentDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);
  const agentListRef = useRef(null);
  const cityListRef = useRef(null);
  // Set briefly to true when we programmatically call input.focus() on a search input,
  // so its onFocus handler can skip auto-opening the dropdown. A real user click/focus
  // leaves the flag false and the dropdown opens normally.
  const suppressDropdownOpenOnFocusRef = useRef(false);

  // Rate state for report assignment (variable rate support)
  const [availableRates, setAvailableRates] = useState([]);
  const [loadingRates, setLoadingRates] = useState(false);
  const [selectedRateId, setSelectedRateId] = useState(null);
  const [proofAttributeValues, setProofAttributeValues] = useState([]);
  const rateAbortControllerRef = useRef(null);

  // Filter agents based on search
  const filteredAgents = useMemo(() => {
    if (!agentSearch) return agents;
    const search = agentSearch.toLowerCase();
    return agents.filter(a =>
      `${a.firstName} ${a.lastName}`.toLowerCase().includes(search)
    );
  }, [agents, agentSearch]);

  // Filter cities based on search
  const filteredCities = useMemo(() => {
    if (!citySearch) return cities;
    const search = citySearch.toLowerCase();
    return cities.filter(c =>
      `${c.name}, ${c.stateCode}`.toLowerCase().startsWith(search)
    );
  }, [cities, citySearch]);

  // Filter reports based on search
  const filteredReports = useMemo(() => {
    if (!reportSearch) return activeReports;
    const search = reportSearch.toLowerCase();
    return activeReports.filter(r =>
      r.reportNumber?.toLowerCase().includes(search) ||
      r.powerReportId?.toString().includes(search) ||
      r.agencyName?.toLowerCase().includes(search)
    );
  }, [activeReports, reportSearch]);

  // Check if selected city is in South Carolina (for SC Premium field)
  const isSouthCarolinaCity = useMemo(() => {
    return selectedCity?.stateCode?.toUpperCase() === 'SC';
  }, [selectedCity]);

  const filteredRates = useMemo(() => {
    if (!availableRates.length) return [];
    const amount = parseCurrency(formData.amountOfBond) || 0;
    if (amount <= 0) return availableRates;
    return availableRates.filter(rate => {
      const minOk = !rate.minAmount || rate.minAmount <= amount;
      const maxOk = !rate.maxAmount || rate.maxAmount >= amount;
      return minOk && maxOk;
    });
  }, [availableRates, formData.amountOfBond]);

  const selectedRate = useMemo(() => {
    if (!selectedRateId || filteredRates.length === 0) return null;
    return filteredRates.find(r => r.rateId === selectedRateId) || null;
  }, [selectedRateId, filteredRates]);

  // Check if the selected power can be assigned to a report
  const canAssignToReport = useMemo(() => {
    if (!selectedPowerStatus) return true; // Unknown = allow (validateForm catches it)
    return isPowerReportable(selectedPowerStatus);
  }, [selectedPowerStatus]);

  // Auto-reset report assignment when power becomes non-reportable
  useEffect(() => {
    if (!canAssignToReport && reportAssignment !== 'none') {
      setReportAssignment('none');
    }
    if (reportAssignment === 'none') {
      setAvailableRates([]);
      setSelectedRateId(null);
      setProofAttributeValues([]);
    }
  }, [canAssignToReport, reportAssignment]);

  // Initialize form data when log changes
  useEffect(() => {
    if (log) {
      const execDate = log.dateExecuted ? formatDateForInput(new Date(log.dateExecuted)) : '';
      const dispDate = log.dispositionDate ? formatDateForInput(new Date(log.dispositionDate)) : '';
      const dob = log.defendantDob ? formatDateForInput(new Date(log.defendantDob)) : '';

      setFormData({
        powerNo: log.powerNo || '',
        name: log.name || '',
        defendantFirstName: log.defendantFirstName || '',
        defendantLastName: log.defendantLastName || '',
        defendantSsn: log.defendantSsn ? formatSSN(log.defendantSsn) : '',
        defendantDob: dob,
        offense: log.offense || '',
        dateExecuted: execDate,
        caseNumber: log.caseNumber || '',
        amountOfBond: log.amountOfBond != null ? formatCurrency(log.amountOfBond) : '',
        premiumCharged: log.premiumCharged != null ? formatCurrency(log.premiumCharged) : '',
        companyPremium: log.companyPremium != null ? formatCurrency(log.companyPremium) : '',
        disposition: log.disposition || '',
        dispositionDate: dispDate,
        collateralReceiptNo: log.collateralReceiptNo || '',
        collateral: log.collateral || '',
        agentsName: log.agentsName || '',
        scAmount: ''
      });

      // Initialize date text fields
      setDateExecutedText(log.dateExecuted ? formatDateForDisplay(new Date(log.dateExecuted)) : '');
      setDispositionDateText(log.dispositionDate ? formatDateForDisplay(new Date(log.dispositionDate)) : '');
      setDobText(log.defendantDob ? formatDateForDisplay(new Date(log.defendantDob)) : '');

      // Reset power validation for edits
      setPowerValidation({
        isValidating: false,
        isValid: true,
        alreadyInLog: false,
        message: ''
      });
    } else {
      // Reset form for new entry
      setFormData({
        powerNo: '',
        name: '',
        defendantFirstName: '',
        defendantLastName: '',
        defendantSsn: '',
        defendantDob: '',
        offense: '',
        dateExecuted: '',
        caseNumber: '',
        amountOfBond: '',
        premiumCharged: '',
        companyPremium: '',
        disposition: '',
        dispositionDate: '',
        collateralReceiptNo: '',
        collateral: '',
        agentsName: '',
        scAmount: ''
      });
      setDateExecutedText('');
      setDispositionDateText('');
      setDobText('');
      setScAmountDisplay('');
      setPowerValidation({
        isValidating: false,
        isValid: null,
        alreadyInLog: false,
        message: ''
      });

      // Reset power dropdown state
      setPowerSearch('');
      setAvailablePowers([]);
      setShowPowerDropdown(false);
      setHighlightedPowerIndex(-1);
      setSelectedPowerStatus(null);

      // Reset report assignment state
      setReportAssignment('none');
      setSelectedReportId(null);
      setSelectedAgent(null);
      setSelectedCity(null);
      setAgentSearch('');
      setCitySearch('');
      setReportSearch('');

    }
    setErrors({});
  }, [log, isOpen]);

  // Focus the power number input field
  const focusPowerField = useCallback(() => {
    const focusTimeout = setTimeout(() => {
      if (firstFieldRef.current) {
        firstFieldRef.current.focus();
      }
    }, 100);
    return focusTimeout;
  }, []);

  // Auto-focus first field after modal opens
  useEffect(() => {
    if (isOpen && !isEdit) {
      const focusTimeout = focusPowerField();
      return () => clearTimeout(focusTimeout);
    }
  }, [isOpen, isEdit, focusPowerField]);

  // In edit mode, fetch the current power status so the discharge UI can
  // determine eligibility. The log row itself doesn't carry powerStatusCd.
  useEffect(() => {
    if (!isOpen || !isEdit || !log?.powerNo || !agencyId) return;
    let cancelled = false;
    (async () => {
      const result = await validatePowerNumber(agencyId, log.powerNo);
      if (!cancelled && result.success) {
        setSelectedPowerStatus(result.powerStatusCd || null);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, isEdit, log?.powerNo, agencyId]);

  // Server-side search: fetch powers when debounced search term changes
  useEffect(() => {
    if (!isOpen || isEdit || !agencyId) return;

    if (!debouncedPowerSearch || debouncedPowerSearch.length < 2) {
      setAvailablePowers([]);
      return;
    }

    let cancelled = false;
    const searchPowers = async () => {
      try {
        setLoadingPowers(true);
        const result = await getAllPowersForLog(agencyId, debouncedPowerSearch);
        if (cancelled) return;
        if (result.success) {
          const powers = Array.isArray(result.data) ? result.data : [];
          setAvailablePowers(powers);
          if (powers.length > 0) {
            setShowPowerDropdown(true);
            setHighlightedPowerIndex(0);
          }
        } else {
          setAvailablePowers([]);
        }
      } catch (error) {
        if (!cancelled) {
          setAvailablePowers([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingPowers(false);
        }
      }
    };

    searchPowers();
    return () => { cancelled = true; };
  }, [isOpen, isEdit, agencyId, debouncedPowerSearch]);

  // Fetch and apply stored data for an executed (E) power. Selecting/validating a power
  // is authoritative, so this OVERWRITES the mapped fields with the power's stored values.
  // Only called when the power's current status is "E" (see callers). The prefillPowerRef
  // guard drops a stale response if the user has changed the power since the request.
  const fetchAndApplyPrefill = useCallback(async (powerNo) => {
    if (!agencyId || !powerNo) return;
    prefillPowerRef.current = powerNo;

    const result = await getExecutedPowerPrefill(agencyId, powerNo);

    // Ignore if the user moved on to a different power, or nothing came back
    if (prefillPowerRef.current !== powerNo) return;
    if (!result.success || !result.data) return;

    const p = result.data;
    setFormData(prev => ({
      ...prev,
      name: p.name || '',
      dateExecuted: p.dateExecuted ? formatDateForInput(new Date(p.dateExecuted)) : '',
      caseNumber: p.caseNumber || '',
      amountOfBond: p.amountOfBond != null ? formatCurrency(p.amountOfBond) : '',
      premiumCharged: p.premiumCharged != null ? formatCurrency(p.premiumCharged) : '',
      agentsName: p.agentsName || '',
    }));
    setDateExecutedText(p.dateExecuted ? formatDateForDisplay(new Date(p.dateExecuted)) : '');
    toast.info('Filled details from executed power — review before saving.');
  }, [agencyId]);

  const handlePowerSelected = useCallback((selectedPower) => {
    if (selectedPower) {
      setFormData(prev => ({ ...prev, powerNo: selectedPower.powerNumber }));
      setPowerSearch(selectedPower.powerNumber);
      setSelectedPowerStatus(selectedPower.powerStatusCd || null);
      // Pull stored data for executed powers so the user doesn't re-type it
      if (selectedPower.powerStatusCd === 'E') {
        fetchAndApplyPrefill(selectedPower.powerNumber);
      } else {
        prefillPowerRef.current = null;
      }
    } else {
      setFormData(prev => ({ ...prev, powerNo: '' }));
      setPowerSearch('');
      setSelectedPowerStatus(null);
      prefillPowerRef.current = null;
    }
    setShowPowerDropdown(false);
    setHighlightedPowerIndex(-1);
    // Dropdown entries come from a server-validated source, mark as valid
    setPowerValidation({
      isValidating: false,
      isValid: selectedPower ? true : null,
      alreadyInLog: false,
      message: ''
    });
    setErrors(prev => {
      const { powerNo, ...rest } = prev;
      return rest;
    });
  }, [fetchAndApplyPrefill]);

  const handlePowerKeyDown = useCallback((e) => {
    if (!showPowerDropdown || availablePowers.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowPowerDropdown(true);
        setHighlightedPowerIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedPowerIndex(prev =>
          prev < availablePowers.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedPowerIndex(prev =>
          prev > 0 ? prev - 1 : availablePowers.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedPowerIndex >= 0 && highlightedPowerIndex < availablePowers.length) {
          handlePowerSelected(availablePowers[highlightedPowerIndex]);
        }
        break;
      case 'Tab':
        if (highlightedPowerIndex >= 0 && highlightedPowerIndex < availablePowers.length) {
          handlePowerSelected(availablePowers[highlightedPowerIndex]);
        }
        setShowPowerDropdown(false);
        break;
      case 'Escape':
        setShowPowerDropdown(false);
        setHighlightedPowerIndex(-1);
        break;
      default:
        break;
    }
  }, [showPowerDropdown, availablePowers, highlightedPowerIndex, handlePowerSelected]);

  // Load agents and cities when report assignment is selected
  useEffect(() => {
    if (reportAssignment !== 'none' && agencyId) {
      loadAgentsAndCities(agencyId);
    }
  }, [reportAssignment, agencyId]);

  // Load active reports when "existing" is selected
  useEffect(() => {
    if (reportAssignment === 'existing' && agencyId) {
      loadActiveReports(agencyId);
    }
  }, [reportAssignment, agencyId]);

  // Prefill defendant first/last name from the main form's full name field
  // when the user switches from "Log only" to a report option
  useEffect(() => {
    if (reportAssignment === 'none') return;

    setFormData(prev => {
      // Don't overwrite if user has already typed something
      if (prev.defendantFirstName || prev.defendantLastName) return prev;
      if (!prev.name?.trim()) return prev;

      const parts = prev.name.trim().split(/\s+/);
      const first = (parts[0] || '').replace(/[^a-zA-Z0-9\s\-']/g, '');
      const last = parts.slice(1).join(' ').replace(/[^a-zA-Z0-9\s\-']/g, '');

      return { ...prev, defendantFirstName: first, defendantLastName: last };
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportAssignment]);

  // Auto-focus the first relevant input when the user picks a report option —
  // "existing" → focus Select Report search, "new" → focus Court City search.
  // Lets the user flow directly into the sub-fields without an extra click.
  useEffect(() => {
    if (reportAssignment === 'none') return;

    const timer = setTimeout(() => {
      const ref = reportAssignment === 'existing' ? reportDropdownRef : cityDropdownRef;
      const input = ref.current?.querySelector('input');
      if (input) {
        suppressDropdownOpenOnFocusRef.current = true;
        input.focus();
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [reportAssignment]);

  // Prefill executing agent from the main form's agentsName when agents finish loading
  useEffect(() => {
    if (loadingAgents || !agents.length) return;
    if (reportAssignment === 'none') return;
    if (selectedAgent || agentSearch) return;

    const agentName = formData.agentsName;
    if (!agentName?.trim()) return;

    const matched = findMatchingAgent(agentName, agents);
    if (matched) {
      handleAgentSelected(matched);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingAgents, agents]);

  const loadAgentsAndCities = async (targetAgencyId) => {
    if (!targetAgencyId) return;
    setLoadingAgents(true);
    try {
      const [agentsResult, citiesResult] = await Promise.all([
        getExecutingAgents(targetAgencyId),
        getCities(targetAgencyId)
      ]);
      if (agentsResult.success) setAgents(agentsResult.data);
      if (citiesResult.success) setCities(citiesResult.data);
    } catch (error) {
      toast.error('Failed to load agents and cities');
    } finally {
      setLoadingAgents(false);
    }
  };

  const loadActiveReports = async (targetAgencyId) => {
    if (!targetAgencyId) return;
    setLoadingReports(true);
    try {
      // Pass includeSubAgencies=false to only get reports from this specific agency
      const result = await getActiveReports(targetAgencyId, 1, 100, '', false);
      if (result.success) {
        // API returns PaginatedResult with 'items' property
        setActiveReports(result.data?.items || []);
      }
    } catch (error) {
      toast.error('Failed to load active reports');
    } finally {
      setLoadingReports(false);
    }
  };

  // Handle click outside to close dropdowns
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (powerDropdownRef.current && !powerDropdownRef.current.contains(event.target)) {
        setShowPowerDropdown(false);
      }
      if (agentDropdownRef.current && !agentDropdownRef.current.contains(event.target)) {
        setShowAgentDropdown(false);
      }
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(event.target)) {
        setShowCityDropdown(false);
      }
      if (reportDropdownRef.current && !reportDropdownRef.current.contains(event.target)) {
        setShowReportDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (highlightedPowerIndex >= 0 && powerListRef.current) {
      const item = powerListRef.current.children[highlightedPowerIndex];
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedPowerIndex]);

  useEffect(() => {
    if (highlightedAgentIndex >= 0 && agentListRef.current) {
      const item = agentListRef.current.children[highlightedAgentIndex];
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedAgentIndex]);

  useEffect(() => {
    if (highlightedCityIndex >= 0 && cityListRef.current) {
      const item = cityListRef.current.children[highlightedCityIndex];
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedCityIndex]);

  useEffect(() => {
    if (highlightedReportIndex >= 0 && reportListRef.current) {
      const item = reportListRef.current.children[highlightedReportIndex];
      if (item) item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedReportIndex]);

  // Validate power number on blur (for new entries only)
  const handlePowerNoBlur = useCallback(async () => {
    if (isEdit) return;

    // Use powerSearch as the value (user may have typed without selecting from dropdown)
    const powerNo = formData.powerNo || powerSearch.trim();
    if (!powerNo) return;

    // If formData.powerNo was empty but user typed something, set it now
    if (!formData.powerNo && powerNo) {
      setFormData(prev => ({ ...prev, powerNo }));
    }

    setPowerValidation({
      isValidating: true,
      isValid: null,
      alreadyInLog: false,
      message: ''
    });

    const result = await validatePowerNumber(agencyId, powerNo);

    if (result.success) {
      setPowerValidation({
        isValidating: false,
        isValid: result.isValidPower,
        alreadyInLog: result.alreadyInLog,
        message: result.message
      });
      setSelectedPowerStatus(result.powerStatusCd || null);

      if (!result.isValidPower || result.alreadyInLog) {
        setErrors(prev => ({
          ...prev,
          powerNo: result.message
        }));
      } else {
        setErrors(prev => {
          const { powerNo, ...rest } = prev;
          return rest;
        });
        // Pull stored data for executed powers so the user doesn't re-type it.
        // Skip if we already prefilled this exact power (e.g. just picked it from
        // the dropdown), so re-validating on blur doesn't double-fetch/toast.
        if (result.powerStatusCd === 'E') {
          if (prefillPowerRef.current !== powerNo) {
            fetchAndApplyPrefill(powerNo);
          }
        } else {
          prefillPowerRef.current = null;
        }
      }
    } else {
      setPowerValidation({
        isValidating: false,
        isValid: null,
        alreadyInLog: false,
        message: ''
      });
    }
  }, [formData.powerNo, powerSearch, agencyId, isEdit, fetchAndApplyPrefill]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Handle currency fields
    if (['amountOfBond', 'premiumCharged', 'companyPremium'].includes(name)) {
      // Allow only numbers and decimal point
      const numericValue = value.replace(/[^0-9.]/g, '');
      setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => {
        const { [name]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  // Handle currency field blur (format)
  const handleCurrencyBlur = (e) => {
    const { name, value } = e.target;
    if (value) {
      const numericValue = parseCurrency(value);
      if (!isNaN(numericValue)) {
        setFormData(prev => ({ ...prev, [name]: formatCurrency(numericValue) }));
      }
    }
  };

  // Handle SC Amount change (special currency field for South Carolina)
  const handleScAmountChange = useCallback((value) => {
    // Allow only numbers and decimal point
    const numericValue = value.replace(/[^0-9.]/g, '');
    setFormData(prev => ({ ...prev, scAmount: numericValue }));
    setScAmountDisplay(numericValue);
    // Clear error for this field
    if (errors.scAmount) {
      setErrors(prev => {
        const { scAmount, ...rest } = prev;
        return rest;
      });
    }
  }, [errors.scAmount]);

  // Handle SC Amount blur (format)
  const handleScAmountBlur = useCallback(() => {
    if (formData.scAmount) {
      const numericValue = parseCurrency(formData.scAmount);
      if (!isNaN(numericValue)) {
        const formatted = formatCurrency(numericValue);
        setFormData(prev => ({ ...prev, scAmount: formatted }));
        setScAmountDisplay(formatted);
      }
    }
  }, [formData.scAmount]);

  // Format rate display for dropdown options
  const formatRateDisplay = useCallback((rate) => {
    if (!rate) return '';
    let rateValue = '';
    if (rate.rateTypeCode === RATE_TYPE.PERCENTAGE) {
      rateValue = `${rate.rate1}%`;
    } else {
      rateValue = `$${rate.rate1?.toLocaleString() || '0'}`;
    }
    const displayName = rate.name ? `${rate.name} (${rateValue})` : rateValue;
    return { displayName, rateValue, isVariable: rate.priority === RATE_PRIORITY.VARIABLE, fullDisplay: displayName };
  }, []);

  // Load available rates for report assignment (variable rate support)
  const loadRates = useCallback(async () => {
    const powerNo = formData.powerNo;
    const state = selectedCity?.stateCode;
    const execDate = formData.dateExecuted;
    const amount = parseCurrency(formData.amountOfBond) || 0;

    if (!powerNo || !state || !execDate || amount <= 0) return;

    if (rateAbortControllerRef.current) rateAbortControllerRef.current.abort();
    const abortController = new AbortController();
    rateAbortControllerRef.current = abortController;

    try {
      setLoadingRates(true);
      const result = await getAvailableRates(powerNo, state, execDate, amount);
      if (abortController.signal.aborted) return;

      if (result.success && result.data) {
        const rates = result.data.rates || [];
        setAvailableRates(rates);

        const validRates = rates.filter(rate => {
          const minOk = !rate.minAmount || rate.minAmount <= amount;
          const maxOk = !rate.maxAmount || rate.maxAmount >= amount;
          return minOk && maxOk;
        });

        const currentValid = selectedRateId && validRates.some(r => r.rateId === selectedRateId);
        if (!currentValid) {
          if (result.data.defaultRateId && validRates.some(r => r.rateId === result.data.defaultRateId)) {
            setSelectedRateId(result.data.defaultRateId);
          } else if (validRates.length > 0) {
            const standardRates = validRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE);
            setSelectedRateId((standardRates[0] || validRates[0]).rateId);
          }
        }
      } else {
        setAvailableRates([]);
      }
    } catch (error) {
      if (!abortController.signal.aborted) setAvailableRates([]);
    } finally {
      if (!abortController.signal.aborted) setLoadingRates(false);
    }
  }, [formData.powerNo, formData.dateExecuted, formData.amountOfBond, selectedCity, selectedRateId]);

  const loadRatesRef = useRef(loadRates);
  loadRatesRef.current = loadRates;

  // Debounced rate loading - only when report assignment is active and all required fields present
  useEffect(() => {
    if (!isOpen || reportAssignment === 'none') return;
    const powerNo = formData.powerNo;
    const state = selectedCity?.stateCode;
    const execDate = formData.dateExecuted;
    const amount = parseCurrency(formData.amountOfBond) || 0;

    if (!powerNo || !state || !execDate || amount <= 0) {
      setAvailableRates([]);
      return;
    }

    const timeoutId = setTimeout(() => loadRatesRef.current(), 500);
    return () => clearTimeout(timeoutId);
  }, [isOpen, reportAssignment, formData.powerNo, formData.dateExecuted, formData.amountOfBond, selectedCity?.stateCode]);

  // Re-select rate when amount changes and current selection is no longer valid
  useEffect(() => {
    if (!isOpen || reportAssignment === 'none' || availableRates.length === 0) return;
    if (selectedRateId && !filteredRates.some(r => r.rateId === selectedRateId)) {
      if (filteredRates.length > 0) {
        const standardRates = filteredRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE);
        setSelectedRateId((standardRates[0] || filteredRates[0]).rateId);
        setProofAttributeValues([]);
      } else {
        setSelectedRateId(null);
        setProofAttributeValues([]);
      }
    }
  }, [isOpen, reportAssignment, filteredRates, selectedRateId, availableRates.length]);

  // Handle rate selection change
  const handleRateChange = useCallback((rateId) => {
    setSelectedRateId(rateId);
    const newRate = filteredRates.find(r => r.rateId === rateId);
    if (newRate?.proofAttributeTypes?.length > 0) {
      setProofAttributeValues(newRate.proofAttributeTypes.map(attr => ({
        proofAttributeTypeId: attr.id,
        value: ''
      })));
    } else {
      setProofAttributeValues([]);
    }
  }, [filteredRates]);

  // Handle proof attribute value change
  const handleProofAttributeChange = useCallback((proofAttributeTypeId, value) => {
    setProofAttributeValues(prev => {
      const idx = prev.findIndex(p => p.proofAttributeTypeId === proofAttributeTypeId);
      if (idx >= 0) {
        const updated = [...prev];
        updated[idx] = { proofAttributeTypeId, value };
        return updated;
      }
      return [...prev, { proofAttributeTypeId, value }];
    });
  }, []);

  // Handle date text changes (dual text + picker pattern)
  const handleDateTextChange = (field, value) => {
    const formatted = formatDateInputText(value);
    if (field === 'dateExecuted') {
      setDateExecutedText(formatted);
    } else if (field === 'dispositionDate') {
      setDispositionDateText(formatted);
    } else if (field === 'defendantDob') {
      setDobText(formatted);
    }
    setErrors(prev => ({ ...prev, [field]: null }));
  };

  // Handle date text blur
  const handleDateTextBlur = (field) => {
    let textValue;
    if (field === 'dateExecuted') {
      textValue = dateExecutedText;
    } else if (field === 'dispositionDate') {
      textValue = dispositionDateText;
    } else if (field === 'defendantDob') {
      textValue = dobText;
    }

    if (!textValue) {
      setFormData(prev => ({ ...prev, [field]: '' }));
      return;
    }

    const parsedDate = parseDateInput(textValue);

    if (parsedDate) {
      setFormData(prev => ({ ...prev, [field]: formatDateForInput(parsedDate) }));
      const fullDateText = formatDateForDisplay(parsedDate);
      if (field === 'dateExecuted') {
        setDateExecutedText(fullDateText);
      } else if (field === 'dispositionDate') {
        setDispositionDateText(fullDateText);
      } else if (field === 'defendantDob') {
        setDobText(fullDateText);
      }
    }
  };

  // Handle agent selection
  const handleAgentSelected = useCallback((agent) => {
    if (agent) {
      setSelectedAgent(agent);
      setAgentSearch(`${agent.firstName} ${agent.lastName}`);
    } else {
      setSelectedAgent(null);
    }
    setShowAgentDropdown(false);
    setHighlightedAgentIndex(-1);
    if (errors.agent) {
      setErrors(prev => {
        const { agent, ...rest } = prev;
        return rest;
      });
    }
  }, [errors.agent]);

  // Handle city selection
  const handleCitySelected = useCallback((city) => {
    if (city) {
      setSelectedCity(city);
      setCitySearch(`${city.name}, ${city.stateCode}`);
    } else {
      setSelectedCity(null);
    }
    setShowCityDropdown(false);
    setHighlightedCityIndex(-1);
    if (errors.city) {
      setErrors(prev => {
        const { city, ...rest } = prev;
        return rest;
      });
    }
  }, [errors.city]);

  // Handle report selection
  const handleReportSelected = useCallback((report) => {
    if (report) {
      setSelectedReportId(report.powerReportId);
      setReportSearch(report.reportNumber || `Report #${report.powerReportId}`);
    } else {
      setSelectedReportId(null);
    }
    setShowReportDropdown(false);
    setHighlightedReportIndex(-1);
    if (errors.reportId) {
      setErrors(prev => {
        const { reportId, ...rest } = prev;
        return rest;
      });
    }
  }, [errors.reportId]);

  // Keyboard navigation for agent dropdown
  const handleAgentKeyDown = useCallback((e) => {
    if (!showAgentDropdown || filteredAgents.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowAgentDropdown(true);
        setHighlightedAgentIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedAgentIndex(prev => prev < filteredAgents.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedAgentIndex(prev => prev > 0 ? prev - 1 : filteredAgents.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedAgentIndex >= 0 && highlightedAgentIndex < filteredAgents.length) {
          handleAgentSelected(filteredAgents[highlightedAgentIndex]);
        }
        break;
      case 'Tab':
        if (highlightedAgentIndex >= 0 && highlightedAgentIndex < filteredAgents.length) {
          handleAgentSelected(filteredAgents[highlightedAgentIndex]);
        }
        setShowAgentDropdown(false);
        break;
      case 'Escape':
        setShowAgentDropdown(false);
        setHighlightedAgentIndex(-1);
        break;
      default:
        break;
    }
  }, [showAgentDropdown, filteredAgents, highlightedAgentIndex, handleAgentSelected]);

  // Keyboard navigation for city dropdown
  const handleCityKeyDown = useCallback((e) => {
    if (!showCityDropdown || filteredCities.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowCityDropdown(true);
        setHighlightedCityIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedCityIndex(prev => prev < filteredCities.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedCityIndex(prev => prev > 0 ? prev - 1 : filteredCities.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedCityIndex >= 0 && highlightedCityIndex < filteredCities.length) {
          handleCitySelected(filteredCities[highlightedCityIndex]);
        }
        break;
      case 'Tab':
        if (highlightedCityIndex >= 0 && highlightedCityIndex < filteredCities.length) {
          handleCitySelected(filteredCities[highlightedCityIndex]);
        }
        setShowCityDropdown(false);
        break;
      case 'Escape':
        setShowCityDropdown(false);
        setHighlightedCityIndex(-1);
        break;
      default:
        break;
    }
  }, [showCityDropdown, filteredCities, highlightedCityIndex, handleCitySelected]);

  // Keyboard navigation for report dropdown
  const handleReportKeyDown = useCallback((e) => {
    if (!showReportDropdown || filteredReports.length === 0) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setShowReportDropdown(true);
        setHighlightedReportIndex(0);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedReportIndex(prev => prev < filteredReports.length - 1 ? prev + 1 : 0);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedReportIndex(prev => prev > 0 ? prev - 1 : filteredReports.length - 1);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedReportIndex >= 0 && highlightedReportIndex < filteredReports.length) {
          handleReportSelected(filteredReports[highlightedReportIndex]);
        }
        break;
      case 'Tab':
        if (highlightedReportIndex >= 0 && highlightedReportIndex < filteredReports.length) {
          handleReportSelected(filteredReports[highlightedReportIndex]);
        }
        setShowReportDropdown(false);
        break;
      case 'Escape':
        setShowReportDropdown(false);
        setHighlightedReportIndex(-1);
        break;
      default:
        break;
    }
  }, [showReportDropdown, filteredReports, highlightedReportIndex, handleReportSelected]);

  // Filter to only allow letters, spaces, hyphens, and apostrophes (for names)
  const lettersOnly = useCallback((str) => {
    if (!str) return str;
    return str.replace(/[^a-zA-Z0-9\s\-']/g, '');
  }, []);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    // Power number is required
    if (!formData.powerNo.trim()) {
      newErrors.powerNo = 'Power number is required';
    } else if (formData.powerNo.length > FIELD_LIMITS.POWER_NO) {
      newErrors.powerNo = `Power number cannot exceed ${FIELD_LIMITS.POWER_NO} characters`;
    }

    // Power number validation (the async existence/duplicate check) is purely
    // informational here — it gates the Discharge buttons but does NOT block
    // saving a Power Log entry. Let the backend return a precise error if the
    // number is invalid or duplicated.

    // Field length validations
    if (formData.caseNumber && formData.caseNumber.length > FIELD_LIMITS.CASE_NUMBER) {
      newErrors.caseNumber = `Case number cannot exceed ${FIELD_LIMITS.CASE_NUMBER} characters`;
    }
    if (formData.collateralReceiptNo && formData.collateralReceiptNo.length > FIELD_LIMITS.COLLATERAL_RECEIPT_NO) {
      newErrors.collateralReceiptNo = `Collateral receipt number cannot exceed ${FIELD_LIMITS.COLLATERAL_RECEIPT_NO} characters`;
    }
    if (formData.agentsName && formData.agentsName.length > FIELD_LIMITS.AGENTS_NAME) {
      newErrors.agentsName = `Agent name cannot exceed ${FIELD_LIMITS.AGENTS_NAME} characters`;
    }
    if (formData.name && formData.name.length > FIELD_LIMITS.DEFENDANT_NAME) {
      newErrors.name = `Defendant name cannot exceed ${FIELD_LIMITS.DEFENDANT_NAME} characters`;
    }

    // Date validations
    if (formData.dateExecuted) {
      const execDate = new Date(formData.dateExecuted);
      if (execDate > new Date()) {
        newErrors.dateExecuted = 'Execution date cannot be in the future';
      }
    }
    if (formData.dispositionDate) {
      const dispDate = new Date(formData.dispositionDate);
      if (dispDate > new Date()) {
        newErrors.dispositionDate = 'Disposition date cannot be in the future';
      }
    }

    // Currency validations
    ['amountOfBond', 'premiumCharged', 'companyPremium'].forEach(field => {
      if (formData[field]) {
        const numValue = parseCurrency(formData[field]);
        if (isNaN(numValue)) {
          newErrors[field] = 'Must be a valid number';
        } else if (numValue < 0) {
          newErrors[field] = 'Cannot be negative';
        }
      }
    });

    // If report assignment is selected, validate city, agent, defendant names, and power status
    if (!isEdit && reportAssignment !== 'none') {
      if (!selectedPowerStatus) {
        newErrors.powerNo = 'Power status could not be determined. Please re-enter the power number.';
      } else if (!isPowerReportable(selectedPowerStatus)) {
        newErrors.powerNo = `Power status "${selectedPowerStatus}" cannot be added to a report`;
      }
      if (!selectedCity) {
        newErrors.city = 'Court city is required for report submission';
      }
      if (!selectedAgent) {
        newErrors.agent = 'Executing agent is required for report submission';
      }
      if (reportAssignment === 'existing' && !selectedReportId) {
        newErrors.reportId = 'Please select a report';
      }

      // Defendant name validation - required for report submission
      if (!formData.defendantFirstName.trim()) {
        newErrors.defendantFirstName = 'Defendant first name is required for report submission';
      }
      if (!formData.defendantLastName.trim()) {
        newErrors.defendantLastName = 'Defendant last name is required for report submission';
      }

      // SC Premium validation - required when city is in South Carolina
      if (isSouthCarolinaCity) {
        if (!formData.scAmount || parseCurrency(formData.scAmount) <= 0) {
          newErrors.scAmount = 'SC Premium is required and must be greater than $0';
        }
      }

      // Validate required proof attributes for variable rate
      if (selectedRate?.priority === RATE_PRIORITY.VARIABLE && selectedRate?.proofAttributeTypes?.length > 0) {
        selectedRate.proofAttributeTypes.forEach(attr => {
          if (attr.required) {
            const val = proofAttributeValues.find(v => v.proofAttributeTypeId === attr.id);
            if (!val?.value?.trim()) {
              newErrors[`proofAttr_${attr.id}`] = `${attr.name} is required`;
            }
          }
        });
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Check if form has unsaved changes
  const isFormDirty = useCallback(() => {
    return Object.keys(INITIAL_FORM_DATA).some(key => formData[key] !== INITIAL_FORM_DATA[key]);
  }, [formData]);

  // Reset all form state to initial values
  const resetForm = useCallback(() => {
    setFormData(INITIAL_FORM_DATA);
    setDateExecutedText('');
    setDispositionDateText('');
    setDobText('');
    setScAmountDisplay('');
    setErrors({});
    setPowerValidation({
      isValidating: false,
      isValid: null,
      alreadyInLog: false,
      message: ''
    });
    setPowerSearch('');
    setAvailablePowers([]);
    setShowPowerDropdown(false);
    setHighlightedPowerIndex(-1);
    setSelectedPowerStatus(null);
    setReportAssignment('none');
    setSelectedReportId(null);
    setSelectedAgent(null);
    setSelectedCity(null);
    setAgentSearch('');
    setCitySearch('');
    setReportSearch('');
    setAvailableRates([]);
    setSelectedRateId(null);
    setProofAttributeValues([]);
    setLoadingRates(false);
    if (rateAbortControllerRef.current) {
      rateAbortControllerRef.current.abort();
      rateAbortControllerRef.current = null;
    }
  }, []);

  // Handle close — show stylized confirm dialog if form has unsaved changes
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const handleClose = useCallback(() => {
    if (isFormDirty()) {
      setShowDiscardConfirm(true);
      return;
    }
    resetForm();
    onClose();
  }, [onClose, isFormDirty, resetForm]);

  const confirmDiscardAndClose = useCallback(() => {
    setShowDiscardConfirm(false);
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      // Prepare payload for power log
      const payload = {
        agencyId,
        powerNo: formData.powerNo.trim(),
        name: formData.name.trim() || null,
        defendantSsn: formData.defendantSsn || null,
        defendantDob: formData.defendantDob || null,
        offense: formData.offense.trim() || null,
        dateExecuted: formData.dateExecuted || null,
        caseNumber: formData.caseNumber.trim() || null,
        amountOfBond: formData.amountOfBond ? parseCurrency(formData.amountOfBond) : null,
        premiumCharged: formData.premiumCharged ? parseCurrency(formData.premiumCharged) : null,
        companyPremium: formData.companyPremium ? parseCurrency(formData.companyPremium) : null,
        disposition: formData.disposition.trim() || null,
        dispositionDate: formData.dispositionDate || null,
        collateralReceiptNo: formData.collateralReceiptNo.trim() || null,
        collateral: formData.collateral.trim() || null,
        agentsName: formData.agentsName.trim() || null
      };

      let result;
      if (isEdit) {
        result = await updatePowerLog(log.logId, payload);
      } else {
        result = await createPowerLog(payload);
      }

      if (result.success) {
        // Handle report assignment for new entries
        if (!isEdit && reportAssignment !== 'none') {
          await handleReportAssignment();
        }

        toast.success(isEdit ? 'Power log entry updated successfully' : 'Power log entry created successfully');
        onSave();
      } else {
        toast.error(result.error || `Failed to ${isEdit ? 'update' : 'create'} power log entry`);
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error(`Failed to ${isEdit ? 'update' : 'create'} power log entry`);
    } finally {
      setIsSaving(false);
    }
  };

  // Handle report assignment after power log is created
  const handleReportAssignment = async () => {
    try {
      let targetReportId = selectedReportId;

      const reportAgencyId = agencyId;

      // Create new report if needed (for the power's agency)
      if (reportAssignment === 'new') {
        const createResult = await createReport(reportAgencyId);
        if (createResult.success && createResult.data?.powerReportId) {
          targetReportId = createResult.data.powerReportId;
          toast.success('New report created');
        } else {
          toast.warning('Power logged but failed to create new report');
          return;
        }
      }

      // Add power to report
      if (targetReportId) {
        const powerReportPayload = {
          powerReportId: targetReportId,
          powerNumber: formData.powerNo.trim(),
          powerStatusCd: selectedPowerStatus || 'U',
          powerAmount: formData.amountOfBond ? parseCurrency(formData.amountOfBond) : 0,
          executionDate: formData.dateExecuted || formatDateForInput(new Date()),
          caseNumber: formData.caseNumber.trim() || null,
          offense: formData.offense.trim() || null,
          defendantFirstName: formData.defendantFirstName.trim(),
          defendantLastName: formData.defendantLastName.trim(),
          defendantDob: formData.defendantDob || null,
          defendantSsn: formData.defendantSsn || null,
          cityId: selectedCity?.cityId,
          executingAgentId: selectedAgent?.executingAgentId,
          executingState: selectedCity?.stateCode || '',
          // Include SC Premium if South Carolina city is selected
          scamount: isSouthCarolinaCity && formData.scAmount ? parseCurrency(formData.scAmount) : null,
          // Variable Rate support
          rateId: selectedRateId || null,
          proofAttributeValues: proofAttributeValues.filter(v => v.value) || null
        };

        const addResult = await addPowerToReport(powerReportPayload);
        if (addResult.success) {
          toast.success(`Power added to ${reportAssignment === 'new' ? 'new' : 'existing'} report`);
        } else {
          toast.warning('Power logged but failed to add to report: ' + (addResult.error || 'Unknown error'));
        }
      }
    } catch (error) {
      console.error('Report assignment error:', error);
      toast.warning('Power logged but report assignment failed');
    }
  };

  // Handle Save + New
  const handleSaveAndNew = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        agencyId,
        powerNo: formData.powerNo.trim(),
        name: formData.name.trim() || null,
        defendantSsn: formData.defendantSsn || null,
        defendantDob: formData.defendantDob || null,
        offense: formData.offense.trim() || null,
        dateExecuted: formData.dateExecuted || null,
        caseNumber: formData.caseNumber.trim() || null,
        amountOfBond: formData.amountOfBond ? parseCurrency(formData.amountOfBond) : null,
        premiumCharged: formData.premiumCharged ? parseCurrency(formData.premiumCharged) : null,
        companyPremium: formData.companyPremium ? parseCurrency(formData.companyPremium) : null,
        disposition: formData.disposition.trim() || null,
        dispositionDate: formData.dispositionDate || null,
        collateralReceiptNo: formData.collateralReceiptNo.trim() || null,
        collateral: formData.collateral.trim() || null,
        agentsName: formData.agentsName.trim() || null
      };

      const result = await createPowerLog(payload);

      if (result.success) {
        // Handle report assignment
        if (reportAssignment !== 'none') {
          await handleReportAssignment();
        }

        toast.success('Power log entry created successfully');

        // Reset form for new entry
        resetForm();

        // Re-focus the power number field for next entry
        focusPowerField();

        // Notify parent to refresh list (without closing modal)
        onRefresh();
      } else {
        toast.error(result.error || 'Failed to create power log entry');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('Failed to create power log entry');
    } finally {
      setIsSaving(false);
    }
  };

  // ---------------------------------------------------------------
  // Discharge actions — mirror the Power Inquiry "Discharge Now" flow.
  // ---------------------------------------------------------------
  // Statuses a power CANNOT be discharged from (already discharged or voided).
  // Anything else is at least eligible to attempt — server-side validation
  // inside DischargeModal is still the source of truth.
  const NON_DISCHARGEABLE_STATUSES = useMemo(
    () => new Set(['D', 'UD', 'V']),
    [],
  );
  const canDischargeCurrentPower = !!formData.powerNo
    && !!selectedPowerStatus
    && !NON_DISCHARGEABLE_STATUSES.has(selectedPowerStatus.toUpperCase());

  const [isDischargeOpen, setIsDischargeOpen] = useState(false);
  const [isQueuingDischarge, setIsQueuingDischarge] = useState(false);
  // Proactive discharge eligibility: runs POST /oath/discharge/validate whenever
  // the user settles on a power. Result gates both Discharge buttons and feeds
  // the tooltip shown on hover when a power can't be discharged.
  // null = unknown/unchecked, { isValid: true } | { isValid: false, reason: '...' }
  const [dischargeEligibility, setDischargeEligibility] = useState(null);
  const [isCheckingEligibility, setIsCheckingEligibility] = useState(false);
  // Locks the discharge actions for this power once the user has either discharged it
  // or added it to the queue — prevents double-submits. Tracks which action was taken
  // so the UI can reflect it ('discharged' | 'queued' | null).
  const [dischargeState, setDischargeState] = useState(null);

  // Reset the lock whenever the user picks a different power, re-opens the modal,
  // or clears the selection.
  useEffect(() => {
    setDischargeState(null);
  }, [formData.powerNo, isOpen]);

  // Build a power object in the shape DischargeModal expects, from current form data.
  const dischargePower = useMemo(() => {
    if (!formData.powerNo) return null;
    const nameParts = (formData.name || '').trim().split(/\s+/).filter(Boolean);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ');
    const bond = parseCurrency(formData.amountOfBond);
    return {
      powerNo: formData.powerNo,
      powerStatus: selectedPowerStatus || null,
      agencyId,
      defendantFirstName: firstName,
      defendantLastName: lastName,
      defendantFullName: formData.name || null,
      bondAmount: bond ?? null,
      powerAmount: bond ?? null,
    };
  }, [formData.powerNo, formData.name, formData.amountOfBond, selectedPowerStatus, agencyId]);

  // Debounce the power number so we don't call /validate on every keystroke.
  const debouncedDischargePowerNo = useDebounce(formData.powerNo, 500);

  // Clear eligibility immediately when the raw power number changes so stale
  // results from a prior power don't linger during the debounce window, and
  // so the caption below can hide until the new check resolves.
  useEffect(() => {
    setDischargeEligibility(null);
  }, [formData.powerNo]);

  // Proactive eligibility check: when the user lands on a power we might want
  // to discharge, ask the server whether it's actually dischargeable (already
  // queued, already discharged, batch still open, etc.). Result drives the
  // disabled state + hover tooltip on the two discharge buttons below.
  useEffect(() => {
    if (!isEdit || !canAccessDischarges || !canDischargeCurrentPower || dischargeState) {
      setDischargeEligibility(null);
      setIsCheckingEligibility(false);
      return undefined;
    }
    if (!debouncedDischargePowerNo || !agencyId) {
      setDischargeEligibility(null);
      return undefined;
    }

    let cancelled = false;
    setIsCheckingEligibility(true);
    (async () => {
      try {
        const result = await validateDischargePowers(
          [debouncedDischargePowerNo],
          undefined,
          undefined,
          { agencyId },
        );
        if (cancelled) return;
        const validation = result.success ? result.data?.validations?.[0] : null;
        if (validation) {
          setDischargeEligibility(
            validation.isValid === false
              ? {
                isValid: false,
                reason: validation.validationErrors?.[0] || 'This power cannot be discharged.',
              }
              : { isValid: true },
          );
        } else {
          setDischargeEligibility(null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Discharge eligibility check error:', err);
          setDischargeEligibility(null);
        }
      } finally {
        if (!cancelled) setIsCheckingEligibility(false);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedDischargePowerNo, canDischargeCurrentPower, canAccessDischarges, agencyId, dischargeState, isEdit]);

  const dischargeBlocked = dischargeEligibility?.isValid === false;
  const dischargeTooltip = isCheckingEligibility
    ? 'Checking discharge eligibility...'
    : dischargeBlocked
      ? dischargeEligibility.reason
      : '';

  const handleDischargeNow = useCallback(() => {
    if (!formData.powerNo || dischargeState) return;
    setIsDischargeOpen(true);
  }, [formData.powerNo, dischargeState]);

  const handleDischargeSuccess = useCallback(() => {
    setIsDischargeOpen(false);
    setDischargeState('discharged');
    toast.success('Power discharged.');
    onRefresh?.();
  }, [onRefresh]);

  const handleAddToDischargeQueue = useCallback(async () => {
    if (!formData.powerNo || isQueuingDischarge || dischargeState) return;
    setIsQueuingDischarge(true);
    try {
      const result = await addToDischargeQueue(formData.powerNo, {
        agencyId,
        defendantName: formData.name || null,
        liability: parseCurrency(formData.amountOfBond) ?? null,
        status: selectedPowerStatus || null,
      });
      if (result.success) {
        setDischargeState('queued');
        toast.success(
          <div>
            <div>Power <strong>{formData.powerNo}</strong> added to discharge queue.</div>
            <a
              href="/app/discharges"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-1 underline font-semibold"
              onClick={(e) => e.stopPropagation()}
            >
              Go to Discharges
            </a>
          </div>,
          { autoClose: 6000 },
        );
      } else {
        toast.error(result.error || 'Failed to add to discharge queue');
      }
    } catch (err) {
      console.error('Add to discharge queue error:', err);
      toast.error('Failed to add to discharge queue');
    } finally {
      setIsQueuingDischarge(false);
    }
  }, [formData.powerNo, formData.name, formData.amountOfBond, selectedPowerStatus, agencyId, isQueuingDischarge, dischargeState]);

  // Segmented-control options for Report Assignment — rendered as one horizontal
  // row so all three choices fit on a single line in the narrow sidebar.
  const reportOptions = [
    { value: 'none', label: 'Log Only', icon: DocumentTextIcon, disabled: false, title: 'Log this power without attaching to a report' },
    { value: 'existing', label: 'Existing Report', icon: FolderOpenIcon, disabled: !canAssignToReport, title: 'Add this power to an existing report' },
    { value: 'new', label: 'New Report', icon: SparklesIcon, disabled: !canAssignToReport, title: 'Create a new report and add this power to it' },
  ];

  const reportAssignmentDisabledReason = useMemo(() => {
    if (canAssignToReport) return '';
    if (!selectedPowerStatus) return 'Select a power first.';
    const statusLabel = getPowerStatusBadgeInfo(selectedPowerStatus).label;
    return `Status "${statusLabel}" can't be added to a report. Only Unexecuted, Unexecuted Forfeiture, Unexecuted Judgment, or Unexecuted Discharge powers can be assigned.`;
  }, [canAssignToReport, selectedPowerStatus]);

  const renderPill = (opt, i, arr) => {
    const selected = reportAssignment === opt.value;
    const Icon = opt.icon;
    const isFirst = i === 0;
    const isLast = i === arr.length - 1;
    const radius = isFirst ? 'rounded-l-lg' : isLast ? 'rounded-r-lg' : '';
    const tooltipTitle = opt.disabled ? reportAssignmentDisabledReason : opt.title;
    return (
      <Tooltip key={opt.value} title={tooltipTitle || ''} placement="top" arrow>
        <label
          className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-1 px-2 py-2 text-[11px] leading-tight font-medium border transition-all duration-150 ${radius} ${
            opt.disabled
              ? 'opacity-50 cursor-not-allowed bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-500'
              : selected
                ? 'bg-primary text-white border-primary shadow-sm relative z-10 cursor-pointer'
                : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-primary/5 hover:border-primary/40 cursor-pointer'
          }`}
        >
          <input
            type="radio"
            name="reportAssignment"
            value={opt.value}
            checked={selected}
            onChange={(e) => setReportAssignment(e.target.value)}
            disabled={opt.disabled}
            className="sr-only"
          />
          <Icon className={`h-4 w-4 shrink-0 transition-transform duration-150 ${selected ? 'scale-110' : ''}`} />
          <span className="truncate w-full text-center">{opt.label}</span>
        </label>
      </Tooltip>
    );
  };

  const powerStatusWarning = !canAssignToReport && selectedPowerStatus && (
    <div className="basis-full flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
      <ExclamationCircleIcon className="h-4 w-4 mt-0.5 shrink-0 text-amber-500 dark:text-amber-400" />
      <div className="text-xs leading-snug">
        <div className="font-semibold">
          Status{' '}
          <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-200/70 dark:bg-amber-800/50 text-[11px] font-medium">
            {getPowerStatusBadgeInfo(selectedPowerStatus).label}
          </span>{' '}
          can&apos;t be added to a report.
        </div>
        <div className="mt-0.5 text-amber-700/90 dark:text-amber-400/90">
          Only Unexecuted, Unexecuted Forfeiture, Unexecuted Judgment, or Unexecuted Discharge powers can be assigned.
        </div>
      </div>
    </div>
  );

  return (
    <>
    <OathModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEdit ? 'Edit Power Log Entry' : 'Add Power Log Entry'}
      size="xl"
      className="sm:max-w-6xl"
      allowOverflow
      persistent
      footer={
        <div className="flex flex-col sm:flex-row flex-wrap gap-2 w-full">
          <OathButton
            variant="danger"
            onClick={handleClose}
            disabled={isSaving}
            className="w-full sm:w-auto order-last sm:order-first"
          >
            Cancel
          </OathButton>

          <div className="flex-1 hidden sm:block" />

          {isEdit ? (
            <OathButton onClick={handleSave} disabled={isSaving}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <OathSpinner size="sm" />
                  Updating...
                </span>
              ) : (
                'Save Changes'
              )}
            </OathButton>
          ) : (
            <>
              <OathButton
                variant="outline"
                onClick={handleSaveAndNew}
                disabled={isSaving}
                size="sm"
              >
                Save + New
              </OathButton>
              <OathButton
                variant="secondary"
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? (
                  <span className="flex items-center gap-2">
                    <OathSpinner size="sm" />
                    Saving...
                  </span>
                ) : (
                  'Save + Close'
                )}
              </OathButton>
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col lg:flex-row lg:gap-4">
      <div className="flex-1 min-w-0">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
        {/* Subtle primary-gradient accent bar — a little "wow" hairline at the top of the form */}
        <div className="md:col-span-4 h-0.5 bg-gradient-to-r from-primary via-primary/50 to-transparent rounded-full -mt-1 mb-0.5" />
        {/* Snapshot-behavior notice — visible upfront so users understand what "Save" actually persists */}
        <div className="md:col-span-4 flex items-center gap-1.5 -mt-1 text-[11px] leading-snug text-gray-500 dark:text-gray-400">
          <InformationCircleIcon className="h-3.5 w-3.5 shrink-0 text-primary/70" />
          <span>
            Values are snapshotted at entry time — later edits in Execution Reports won&apos;t update this log entry.
          </span>
        </div>
        <div className="contents">
          {/* Power Number - Autocomplete dropdown for new entries, disabled input for edits */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Power Number <span className="text-red-500">*</span>
            </label>
            {isEdit ? (
              // Disabled input for edit mode
              <>
                <input
                  type="text"
                  value={formData.powerNo}
                  disabled
                  className="w-full px-4 py-1.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white cursor-not-allowed"
                />
                {/* Link to active report if power is in one */}
                {log?.activeReportId && (
                  <a
                    href={`/app/power-reports/${log.activeReportId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 mt-1 text-xs text-primary hover:text-primary/80 hover:underline"
                  >
                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                    View in Active Report {log.activeReportNumber ? `(${log.activeReportNumber})` : ''}
                  </a>
                )}
              </>
            ) : (
              // Searchable dropdown for add mode
              <div className="relative" ref={powerDropdownRef}>
                <input
                  ref={firstFieldRef}
                  type="text"
                  value={powerSearch}
                  onChange={(e) => {
                    setPowerSearch(e.target.value);
                    setShowPowerDropdown(true);
                    setHighlightedPowerIndex(0);
                    // Clear formData.powerNo so it gets re-evaluated on blur
                    if (formData.powerNo) {
                      setFormData(prev => ({ ...prev, powerNo: '' }));
                    }
                    // Clear validation when typing
                    setPowerValidation({
                      isValidating: false,
                      isValid: null,
                      alreadyInLog: false,
                      message: ''
                    });
                    setSelectedPowerStatus(null);
                    if (errors.powerNo) {
                      setErrors(prev => {
                        const { powerNo, ...rest } = prev;
                        return rest;
                      });
                    }
                  }}
                  onFocus={() => {
                    if (powerSearch.length >= 2) {
                      setShowPowerDropdown(true);
                      setHighlightedPowerIndex(0);
                    }
                  }}
                  onBlur={handlePowerNoBlur}
                  onKeyDown={handlePowerKeyDown}
                  placeholder="Search power #"
                  autoComplete="off"
                  maxLength={FIELD_LIMITS.POWER_NO}
                  className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.powerNo ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700`}
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                  {loadingPowers ? (
                    <OathSpinner size="sm" />
                  ) : powerValidation.isValidating ? (
                    <OathDualSpinner size="sm" />
                  ) : powerValidation.isValid === true && !powerValidation.alreadyInLog ? (
                    <CheckCircleIcon className="h-5 w-5 text-green-500" />
                  ) : (powerValidation.isValid === false || powerValidation.alreadyInLog) ? (
                    <ExclamationCircleIcon className="h-5 w-5 text-red-500" />
                  ) : null}
                </div>
                {showPowerDropdown && (
                  <div
                    ref={powerListRef}
                    className="absolute z-20 min-w-full w-max max-w-[22rem] mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                  >
                    {powerSearch.length < 2 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        Type 2+ characters to search...
                      </div>
                    ) : loadingPowers ? (
                      <div className="px-4 py-3 flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                        <OathSpinner size="sm" /> Searching...
                      </div>
                    ) : availablePowers.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                        No matching powers found
                      </div>
                    ) : (
                      availablePowers.map((p, index) => (
                        <button
                          key={p.powerNumber}
                          type="button"
                          onMouseDown={(e) => e.preventDefault()}
                          onClick={() => handlePowerSelected(p)}
                          className={`w-full px-4 py-2 text-left whitespace-nowrap ${
                            index === highlightedPowerIndex
                              ? 'bg-primary/10 dark:bg-primary/20'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="font-mono text-gray-900 dark:text-white">{p.powerNumber}</span>
                          <span className={`ml-2 text-xs font-medium px-1.5 py-0.5 rounded ${
                            p.powerStatusCd === 'U' || p.powerStatusCd?.startsWith('U')
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                              : p.powerStatusCd === 'E'
                                ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {p.powerStatusCd}
                          </span>
                          <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                            ${p.denomination?.toLocaleString() || 'N/A'}
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {errors.powerNo && (
              <p className="text-xs text-red-500 mt-1">{errors.powerNo}</p>
            )}
          </div>

          {/* Defendant Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Defendant Name
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              maxLength={FIELD_LIMITS.DEFENDANT_NAME}
              className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.name
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-700`}
              placeholder="Enter defendant name"
            />
            {errors.name ? (
              <p className="text-xs text-red-500 mt-1">{errors.name}</p>
            ) : formData.name.length > 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {formData.name.length}/{FIELD_LIMITS.DEFENDANT_NAME}
              </p>
            ) : null}
          </div>
        </div>

        {/* SSN and DOB */}
        <div className="contents">
          {/* SSN */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SSN
            </label>
            <input
              type="text"
              value={formData.defendantSsn}
              onChange={(e) => {
                const formatted = formatSSN(e.target.value);
                setFormData(prev => ({ ...prev, defendantSsn: formatted }));
                if (errors.defendantSsn) {
                  setErrors(prev => {
                    const { defendantSsn, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              placeholder="XXX-XX-XXXX"
              maxLength={VALIDATION_RULES.MAX_SSN_LENGTH}
              className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.defendantSsn ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-700`}
            />
            {errors.defendantSsn ? (
              <p className="text-xs text-red-500 mt-1">{errors.defendantSsn}</p>
            ) : null}
          </div>

          {/* Date of Birth */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date of Birth
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={dobText}
                onChange={(e) => handleDateTextChange('defendantDob', e.target.value)}
                onBlur={() => handleDateTextBlur('defendantDob')}
                placeholder="MM/DD/YYYY"
                maxLength={10}
                autoComplete="off"
                className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.defendantDob ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => dobDatePickerRef.current?.showPicker()}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <CalendarIcon className="h-5 w-5" />
              </button>
              <input
                type="date"
                ref={dobDatePickerRef}
                tabIndex={-1}
                aria-hidden="true"
                value={formData.defendantDob || ''}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateValue = new Date(e.target.value + 'T00:00:00');
                    setFormData(prev => ({ ...prev, defendantDob: formatDateForInput(dateValue) }));
                    setDobText(formatDateForDisplay(dateValue));
                  }
                }}
                className="sr-only"
              />
            </div>
            {errors.defendantDob ? (
              <p className="text-xs text-red-500 mt-1">{errors.defendantDob}</p>
            ) : null}
          </div>
        </div>

        <div className="contents">
          {/* Date Executed */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Date Executed
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={dateExecutedText}
                onChange={(e) => handleDateTextChange('dateExecuted', e.target.value)}
                onBlur={() => handleDateTextBlur('dateExecuted')}
                placeholder="MM/DD/YYYY"
                maxLength={10}
                autoComplete="off"
                className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.dateExecuted ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => dateExecutedPickerRef.current?.showPicker()}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <CalendarIcon className="h-5 w-5" />
              </button>
              <input
                type="date"
                ref={dateExecutedPickerRef}
                tabIndex={-1}
                aria-hidden="true"
                value={formData.dateExecuted || ''}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateValue = new Date(e.target.value + 'T00:00:00');
                    setFormData(prev => ({ ...prev, dateExecuted: formatDateForInput(dateValue) }));
                    setDateExecutedText(formatDateForDisplay(dateValue));
                  }
                }}
                className="sr-only"
              />
            </div>
            {errors.dateExecuted ? (
              <p className="text-xs text-red-500 mt-1">{errors.dateExecuted}</p>
            ) : null}
          </div>

          {/* Case Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Case Number
            </label>
            <input
              type="text"
              name="caseNumber"
              value={formData.caseNumber}
              onChange={handleChange}
              maxLength={FIELD_LIMITS.CASE_NUMBER}
              className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.caseNumber
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-700`}
              placeholder="Enter case number"
            />
            {errors.caseNumber ? (
              <p className="text-xs text-red-500 mt-1">{errors.caseNumber}</p>
            ) : formData.caseNumber.length > 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {formData.caseNumber.length}/{FIELD_LIMITS.CASE_NUMBER}
              </p>
            ) : null}
          </div>
        </div>

        <div className="contents">
          {/* Bond Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Bond Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="text"
                name="amountOfBond"
                value={formData.amountOfBond}
                onChange={handleChange}
                onBlur={handleCurrencyBlur}
                className={`w-full pl-8 pr-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.amountOfBond
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
                placeholder="0.00"
              />
            </div>
            {errors.amountOfBond && (
              <p className="text-xs text-red-500 mt-1">{errors.amountOfBond}</p>
            )}
          </div>

          {/* Premium Charged */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Premium Charged
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="text"
                name="premiumCharged"
                value={formData.premiumCharged}
                onChange={handleChange}
                onBlur={handleCurrencyBlur}
                className={`w-full pl-8 pr-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.premiumCharged
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
                placeholder="0.00"
              />
            </div>
            {errors.premiumCharged && (
              <p className="text-xs text-red-500 mt-1">{errors.premiumCharged}</p>
            )}
          </div>
        </div>

        <div className="contents">
          {/* Premium to Company */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Premium to Company
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
              <input
                type="text"
                name="companyPremium"
                value={formData.companyPremium}
                onChange={handleChange}
                onBlur={handleCurrencyBlur}
                className={`w-full pl-8 pr-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.companyPremium
                    ? 'border-red-500'
                    : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
                placeholder="0.00"
              />
            </div>
            {errors.companyPremium && (
              <p className="text-xs text-red-500 mt-1">{errors.companyPremium}</p>
            )}
          </div>

          {/* Agent Name */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Agent Name
            </label>
            <input
              type="text"
              name="agentsName"
              value={formData.agentsName}
              onChange={handleChange}
              maxLength={FIELD_LIMITS.AGENTS_NAME}
              className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                errors.agentsName
                  ? 'border-red-500'
                  : 'border-gray-300 dark:border-gray-600'
              } bg-white dark:bg-gray-700`}
              placeholder="Enter agent name"
            />
            {errors.agentsName ? (
              <p className="text-xs text-red-500 mt-1">{errors.agentsName}</p>
            ) : formData.agentsName.length > 0 ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
                {formData.agentsName.length}/{FIELD_LIMITS.AGENTS_NAME}
              </p>
            ) : null}
          </div>
        </div>

        <div className="contents">
          {/* Disposition */}
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Disposition
            </label>
            <input
              type="text"
              name="disposition"
              value={formData.disposition}
              onChange={handleChange}
              className="w-full px-4 py-1.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700"
              placeholder="Enter disposition"
            />
          </div>

          {/* Disposition Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Disposition Date
            </label>
            <div className="relative">
              <input
                type="text"
                inputMode="numeric"
                value={dispositionDateText}
                onChange={(e) => handleDateTextChange('dispositionDate', e.target.value)}
                onBlur={() => handleDateTextBlur('dispositionDate')}
                placeholder="MM/DD/YYYY"
                maxLength={10}
                autoComplete="off"
                className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.dispositionDate ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
              />
              <button
                type="button"
                tabIndex={-1}
                onClick={() => dispositionDatePickerRef.current?.showPicker()}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <CalendarIcon className="h-5 w-5" />
              </button>
              <input
                type="date"
                ref={dispositionDatePickerRef}
                tabIndex={-1}
                aria-hidden="true"
                value={formData.dispositionDate || ''}
                max={new Date().toISOString().split('T')[0]}
                onChange={(e) => {
                  if (e.target.value) {
                    const dateValue = new Date(e.target.value + 'T00:00:00');
                    setFormData(prev => ({ ...prev, dispositionDate: formatDateForInput(dateValue) }));
                    setDispositionDateText(formatDateForDisplay(dateValue));
                  }
                }}
                className="sr-only"
              />
            </div>
            {errors.dispositionDate ? (
              <p className="text-xs text-red-500 mt-1">{errors.dispositionDate}</p>
            ) : null}
          </div>
        </div>

        {/* Collateral Receipt Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Collateral Receipt #
          </label>
          <input
            type="text"
            name="collateralReceiptNo"
            value={formData.collateralReceiptNo}
            onChange={handleChange}
            maxLength={FIELD_LIMITS.COLLATERAL_RECEIPT_NO}
            className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
              errors.collateralReceiptNo
                ? 'border-red-500'
                : 'border-gray-300 dark:border-gray-600'
            } bg-white dark:bg-gray-700`}
            placeholder="Enter receipt number"
          />
          {errors.collateralReceiptNo ? (
            <p className="text-xs text-red-500 mt-1">{errors.collateralReceiptNo}</p>
          ) : formData.collateralReceiptNo.length > 0 ? (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {formData.collateralReceiptNo.length}/{FIELD_LIMITS.COLLATERAL_RECEIPT_NO}
            </p>
          ) : null}
        </div>

        {/* Offense */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Offense
          </label>
          <textarea
            name="offense"
            value={formData.offense}
            onChange={handleChange}
            rows={2}
            maxLength={VALIDATION_RULES.MAX_OFFENSE_LENGTH}
            className="w-full px-4 py-1.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700"
            placeholder="Enter offense/charge description..."
          />
          {formData.offense.length > 0 && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 text-right">
              {formData.offense.length}/{VALIDATION_RULES.MAX_OFFENSE_LENGTH}
            </p>
          )}
        </div>

        {/* Collateral Description - Side by side with Offense */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Collateral Details
          </label>
          <textarea
            name="collateral"
            value={formData.collateral}
            onChange={handleChange}
            rows={2}
            className="w-full px-4 py-1.5 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary bg-white dark:bg-gray-700"
            placeholder="Describe any collateral held..."
          />
        </div>

        {/* Discharge — Discharge Now / Add to Queue, mirrors Power Inquiry flow. */}
        {/* Edit mode only — Add flow does not expose discharge actions. */}
        {isEdit && canAccessDischarges && canDischargeCurrentPower && (
        <div className="md:col-span-4 p-3 bg-gradient-to-br from-blue-50/70 to-primary/5 dark:from-blue-900/20 dark:to-primary/10 rounded-xl border border-blue-200/60 dark:border-blue-800/60">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
              <DocumentMinusIcon className="h-4 w-4" />
              Discharge
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400 mr-auto flex items-center gap-1.5 min-w-0">
              {dischargeState === 'discharged' ? (
                <>
                  <CheckCircleIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {formData.powerNo} discharged.
                  </span>
                </>
              ) : dischargeState === 'queued' ? (
                <>
                  <CheckCircleIcon className="h-3.5 w-3.5 text-green-600 dark:text-green-400" />
                  <span className="text-green-700 dark:text-green-400 font-medium">
                    {formData.powerNo} added to discharge queue.
                  </span>
                </>
              ) : dischargeEligibility?.isValid === true ? (
                `Optionally discharge ${formData.powerNo} now or queue it for later.`
              ) : null}
            </span>
            <Tooltip title={dischargeTooltip} placement="top" arrow>
              <span>
                <button
                  type="button"
                  onClick={handleDischargeNow}
                  disabled={!formData.powerNo || isSaving || isQueuingDischarge || isCheckingEligibility || dischargeBlocked || !!dischargeState}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-blue-300 dark:border-blue-700 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium transition-colors hover:bg-blue-100 dark:hover:bg-blue-900/50 hover:border-blue-400 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isCheckingEligibility ? (
                    <OathSpinner size="sm" />
                  ) : dischargeState === 'discharged' ? (
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                  ) : (
                    <DocumentMinusIcon className="h-3.5 w-3.5" />
                  )}
                  {dischargeState === 'discharged' ? 'Discharged' : 'Discharge Now'}
                </button>
              </span>
            </Tooltip>
            <Tooltip title={dischargeTooltip} placement="top" arrow>
              <span>
                <button
                  type="button"
                  onClick={handleAddToDischargeQueue}
                  disabled={!formData.powerNo || isSaving || isQueuingDischarge || isCheckingEligibility || dischargeBlocked || !!dischargeState}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs font-medium transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-primary/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isQueuingDischarge ? (
                    <OathSpinner size="sm" />
                  ) : dischargeState === 'queued' ? (
                    <CheckCircleIcon className="h-3.5 w-3.5" />
                  ) : (
                    <QueueListIcon className="h-3.5 w-3.5" />
                  )}
                  {dischargeState === 'queued' ? 'Queued' : 'Add to Queue'}
                </button>
              </span>
            </Tooltip>
            {dischargeBlocked && !dischargeState && (
              <div className="basis-full flex items-start gap-2 px-3 py-2 rounded-lg border border-amber-300 dark:border-amber-700/60 bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300">
                <ExclamationCircleIcon className="h-4 w-4 mt-0.5 shrink-0 text-amber-500 dark:text-amber-400" />
                <div className="text-xs leading-snug">
                  <div className="font-semibold">
                    This power can&apos;t be discharged right now.
                  </div>
                  <div className="mt-0.5 text-amber-700/90 dark:text-amber-400/90">
                    {dischargeEligibility.reason}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        )}

      </div>
      </div>

        {/* Report Assignment — now a right sidebar at lg+; grows within its own column instead of pushing the form taller */}
        {!isEdit && showReportAssignment && (
          <div className="lg:w-80 lg:shrink-0">
            <div className="p-3 bg-gradient-to-br from-gray-50 to-primary/5 dark:from-gray-700/50 dark:to-primary/10 rounded-xl border border-gray-200 dark:border-gray-600 flex flex-col gap-3">
              <span className="text-sm font-semibold text-primary dark:text-primary-light">
                Report Assignment
              </span>
              <div className="flex -space-x-px">
                {reportOptions.map(renderPill)}
              </div>
              {powerStatusWarning}

            {/* Report dropdown when "existing" selected */}
            {reportAssignment === 'existing' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select Report <span className="text-red-500">*</span>
                </label>
                <div className="relative" ref={reportDropdownRef}>
                  <input
                    type="text"
                    value={reportSearch}
                    onChange={(e) => {
                      setReportSearch(e.target.value);
                      setShowReportDropdown(true);
                      setHighlightedReportIndex(0);
                    }}
                    onFocus={() => {
                      if (suppressDropdownOpenOnFocusRef.current) {
                        suppressDropdownOpenOnFocusRef.current = false;
                        return;
                      }
                      setShowReportDropdown(true);
                      setHighlightedReportIndex(0);
                    }}
                    onKeyDown={handleReportKeyDown}
                    placeholder="Search reports..."
                    className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      errors.reportId ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700`}
                  />
                  {loadingReports ? (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <OathSpinner size="sm" />
                    </div>
                  ) : (
                    <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none text-gray-500 dark:text-gray-400" />
                  )}
                  {showReportDropdown && filteredReports.length > 0 && (
                    <div
                      ref={reportListRef}
                      className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                    >
                      {filteredReports.map((r, index) => (
                        <button
                          key={r.powerReportId}
                          type="button"
                          onClick={() => handleReportSelected(r)}
                          className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                            index === highlightedReportIndex
                              ? 'bg-primary/10 dark:bg-primary/20'
                              : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                          }`}
                        >
                          <span className="font-medium">{r.reportNumber || `Report #${r.powerReportId}`}</span>
                          {r.agencyName && (
                            <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">
                              {r.agencyName}
                            </span>
                          )}
                          {r.reportDate && (
                            <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">
                              {formatDateForDisplay(new Date(r.reportDate))}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {errors.reportId && (
                  <p className="text-xs text-red-500 mt-1">{errors.reportId}</p>
                )}
              </div>
            )}

            {/* Additional report fields — vertically stacked inside the sidebar card */}
            {reportAssignment !== 'none' && (
              <>
                <div className="h-px bg-gradient-to-r from-primary/30 via-primary/20 to-transparent" />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {reportAssignment === 'new'
                    ? 'A new report will be created and this power auto-added. Fields below don\u2019t overwrite the Power Log snapshot.'
                    : 'These report fields don\u2019t overwrite the Power Log snapshot.'}
                </p>
                <div className="flex flex-col gap-2">

            {/* Court City dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Court City <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={cityDropdownRef}>
                <input
                  type="text"
                  value={citySearch}
                  onChange={(e) => {
                    setCitySearch(lettersOnly(e.target.value));
                    setShowCityDropdown(true);
                    setHighlightedCityIndex(0);
                  }}
                  onFocus={() => {
                    if (suppressDropdownOpenOnFocusRef.current) {
                      suppressDropdownOpenOnFocusRef.current = false;
                      return;
                    }
                    setShowCityDropdown(true);
                    setHighlightedCityIndex(0);
                  }}
                  onKeyDown={handleCityKeyDown}
                  placeholder="Search city..."
                  autoComplete="new-password"
                  className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.city ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700`}
                />
                {loadingAgents ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <OathSpinner size="sm" />
                  </div>
                ) : (
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none text-gray-500 dark:text-gray-400" />
                )}
                {showCityDropdown && filteredCities.length > 0 && (
                  <div
                    ref={cityListRef}
                    className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredCities.map((c, index) => (
                      <button
                        key={`${c.name}-${c.stateCode}`}
                        type="button"
                        onClick={() => handleCitySelected(c)}
                        className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                          index === highlightedCityIndex
                            ? 'bg-primary/10 dark:bg-primary/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {c.name}, {c.stateCode}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.city && (
                <p className="text-xs text-red-500 mt-1">{errors.city}</p>
              )}
            </div>

            {/* Executing Agent dropdown */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Executing Agent <span className="text-red-500">*</span>
              </label>
              <div className="relative" ref={agentDropdownRef}>
                <input
                  type="text"
                  value={agentSearch}
                  onChange={(e) => {
                    setAgentSearch(lettersOnly(e.target.value));
                    setShowAgentDropdown(true);
                    setHighlightedAgentIndex(0);
                  }}
                  onFocus={() => {
                    if (suppressDropdownOpenOnFocusRef.current) {
                      suppressDropdownOpenOnFocusRef.current = false;
                      return;
                    }
                    setShowAgentDropdown(true);
                    setHighlightedAgentIndex(0);
                  }}
                  onKeyDown={handleAgentKeyDown}
                  placeholder="Search agent..."
                  autoComplete="new-password"
                  className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                    errors.agent ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  } bg-white dark:bg-gray-700`}
                />
                {loadingAgents ? (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <OathSpinner size="sm" />
                  </div>
                ) : (
                  <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 h-5 w-5 pointer-events-none text-gray-500 dark:text-gray-400" />
                )}
                {showAgentDropdown && filteredAgents.length > 0 && (
                  <div
                    ref={agentListRef}
                    className="absolute z-20 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-48 overflow-y-auto"
                  >
                    {filteredAgents.map((a, index) => (
                      <button
                        key={a.executingAgentId}
                        type="button"
                        onClick={() => handleAgentSelected(a)}
                        className={`w-full px-4 py-2 text-left text-gray-900 dark:text-white ${
                          index === highlightedAgentIndex
                            ? 'bg-primary/10 dark:bg-primary/20'
                            : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {a.firstName} {a.lastName}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {errors.agent && (
                <p className="text-xs text-red-500 mt-1">{errors.agent}</p>
              )}
            </div>

            {/* Defendant First Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant First Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.defendantFirstName}
                onChange={(e) => {
                  const value = lettersOnly(e.target.value);
                  setFormData(prev => ({ ...prev, defendantFirstName: value }));
                  if (errors.defendantFirstName) {
                    setErrors(prev => {
                      const { defendantFirstName, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="First name"
                autoComplete="off"
                className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.defendantFirstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
              />
              {errors.defendantFirstName && (
                <p className="text-xs text-red-500 mt-1">{errors.defendantFirstName}</p>
              )}
            </div>

            {/* Defendant Last Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Defendant Last Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.defendantLastName}
                onChange={(e) => {
                  const value = lettersOnly(e.target.value);
                  setFormData(prev => ({ ...prev, defendantLastName: value }));
                  if (errors.defendantLastName) {
                    setErrors(prev => {
                      const { defendantLastName, ...rest } = prev;
                      return rest;
                    });
                  }
                }}
                placeholder="Last name"
                autoComplete="off"
                className={`w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                  errors.defendantLastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                } bg-white dark:bg-gray-700`}
              />
              {errors.defendantLastName && (
                <p className="text-xs text-red-500 mt-1">{errors.defendantLastName}</p>
              )}
            </div>

            {/* SC Premium - Only shows when South Carolina city is selected */}
            {isSouthCarolinaCity && (
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Actual SC Premium <span className="text-red-500">*</span>
                </label>
                <div className="relative max-w-xs">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">$</span>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={scAmountDisplay}
                    onChange={(e) => handleScAmountChange(e.target.value)}
                    onBlur={handleScAmountBlur}
                    placeholder="0.00"
                    className={`w-full pl-8 pr-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary ${
                      errors.scAmount ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700`}
                  />
                </div>
                {errors.scAmount ? (
                  <p className="text-xs text-red-500 mt-1">{errors.scAmount}</p>
                ) : (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Required for powers executed in South Carolina
                  </p>
                )}
              </div>
            )}

            {/* Rate Selection (only when more than 1 rate available) */}
            {filteredRates.length > 1 && (
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Rate
                </label>
                <div className="relative max-w-md">
                  <select
                    value={selectedRateId || ''}
                    onChange={(e) => handleRateChange(e.target.value ? parseInt(e.target.value) : null)}
                    disabled={loadingRates}
                    className={`w-full px-4 py-1.5 pr-10 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600 ${loadingRates ? 'bg-gray-100 dark:bg-gray-600 cursor-not-allowed' : 'bg-white dark:bg-gray-700'} appearance-none`}
                  >
                    {filteredRates.filter(r => r.priority !== RATE_PRIORITY.VARIABLE).map(rate => {
                      const display = formatRateDisplay(rate);
                      return <option key={rate.rateId} value={rate.rateId}>{display.fullDisplay}</option>;
                    })}
                    {filteredRates.some(r => r.priority === RATE_PRIORITY.VARIABLE) && (
                      <option disabled>── Variable Rates ──</option>
                    )}
                    {filteredRates.filter(r => r.priority === RATE_PRIORITY.VARIABLE).map(rate => {
                      const display = formatRateDisplay(rate);
                      return <option key={rate.rateId} value={rate.rateId}>{display.fullDisplay}</option>;
                    })}
                  </select>
                  {loadingRates && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <OathSpinner size="sm" />
                    </div>
                  )}
                </div>
                {selectedRate?.priority === RATE_PRIORITY.VARIABLE && (
                  <span className="inline-block text-xs bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 px-2 py-0.5 rounded-full mt-1">
                    Variable Rate
                  </span>
                )}
              </div>
            )}

            {/* Proof Attributes (for Variable Rates) */}
            {selectedRate?.proofAttributeTypes?.length > 0 && (
              <div className="col-span-full border rounded-xl p-4 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800">
                <h4 className="text-sm font-medium text-amber-800 dark:text-amber-300 mb-3">
                  Required Documentation for Variable Rate
                </h4>
                <div className="space-y-3">
                  {selectedRate.proofAttributeTypes.map((attr) => (
                    <div key={attr.id}>
                      <label className="block text-sm text-gray-700 dark:text-gray-300 mb-1">
                        {attr.name}{attr.required && <span className="text-red-500"> *</span>}
                      </label>
                      <input
                        type="text"
                        value={proofAttributeValues.find(v => v.proofAttributeTypeId === attr.id)?.value || ''}
                        onChange={(e) => handleProofAttributeChange(attr.id, e.target.value)}
                        placeholder={attr.name}
                        className="w-full px-4 py-1.5 border rounded-xl text-gray-900 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      />
                      {errors[`proofAttr_${attr.id}`] && (
                        <p className="text-xs text-red-500 mt-1">{errors[`proofAttr_${attr.id}`]}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
                </div>
              </>
            )}
            </div>
          </div>
        )}
      </div>
    </OathModal>

    {/* Discard-changes confirmation — stylized site-cohesive dialog in place of window.confirm. */}
    <OathConfirmDialog
      open={showDiscardConfirm}
      title="Discard unsaved changes?"
      message="You have unsaved changes on this entry. Closing now will lose them."
      confirmLabel="Discard"
      cancelLabel="Keep Editing"
      variant="warning"
      onConfirm={confirmDiscardAndClose}
      onCancel={() => setShowDiscardConfirm(false)}
    />

    {/* Discharge modal portaled above this modal so its internal state and overlay */}
    {/* are isolated from the OathModal wrapper. Mirrors PowerDetailModal's pattern. */}
    {isDischargeOpen && createPortal(
      <div className="relative z-[60]">
        <DischargeModal
          isOpen={isDischargeOpen}
          onClose={() => setIsDischargeOpen(false)}
          power={dischargePower}
          onSuccess={handleDischargeSuccess}
          agencyId={agencyId}
        />
      </div>,
      document.body,
    )}
    </>
  );
};

export default AddEditPowerLogModal;
